import { Component, EventEmitter, Input, Output, ViewChild, OnChanges, SimpleChanges, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatMenuModule, MatMenu } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface SortColumn {
  key: string;
  name: string;
  isChecked?: boolean; // optional initial state
  // sequence is computed based on check order; you can pre-seed if you like
}

export interface ApplySortPayload {
  orderDir: 'asc' | 'desc';
  columns: { key: string; name: string; sequence: number }[];
}

@Component({
  selector: 'app-sort-menu',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatMenuModule,
    MatCheckboxModule,
    MatRadioModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './sort-menu.component.html',
})
export class SortMenuComponent implements OnChanges {
  /** Columns to show in the list */
  @Input() columns: SortColumn[] = [];

  /** Initial order direction (default: 'asc') */
  @Input() orderDir: 'asc' | 'desc' = 'asc';

  @Input() maxSelected = 3;

  /**
   * Optional initial order array.
   * Example: ['name','createdAt'] — will mark them checked in this sequence.
   */
  @Input() initialOrderKeys: string[] = [];

  /** Emits when user clicks Apply (with the selected order) */
  @Output() apply = new EventEmitter<ApplySortPayload>();

  /** Emits when user clicks Clear */
  @Output() clear = new EventEmitter<void>();

  /** Expose the MatMenu so parent can bind [matMenuTriggerFor] */
  @ViewChild('sortingMenu', { static: true }) menu!: MatMenu;

  form!: FormGroup;
  get sortColumns(): FormArray<FormGroup> {
    return this.form.get('SortColumns') as FormArray<FormGroup>;
  }

  // Track click sequence using a simple counter
  private sequenceCounter = 0;
  private sequenceMap = new Map<string, number>(); // key -> sequence

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      SortColumns: this.fb.array([]),
      OrderDir: new FormControl<'asc' | 'desc'>('asc'),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns'] || changes['initialOrderKeys'] || changes['orderDir']) {
      this.rebuildForm();
    }
  }

  private rebuildForm() {
    // reset
    this.sortColumns.clear();
    this.sequenceMap.clear();
    this.sequenceCounter = 0;

    // prime sequence from initialOrderKeys if provided
    if (this.initialOrderKeys?.length) {
      this.initialOrderKeys.forEach((key, idx) => this.sequenceMap.set(key, idx + 1));
      this.sequenceCounter = this.initialOrderKeys.length;
    }

    // build array controls
    for (const c of this.columns) {
      // determine initial checked state
      const isChecked = typeof c.isChecked === 'boolean'
        ? c.isChecked
        : this.sequenceMap.has(c.key);

      // if checked but no sequence yet, assign one
      if (isChecked && !this.sequenceMap.has(c.key)) {
        this.sequenceMap.set(c.key, ++this.sequenceCounter);
      }

      this.sortColumns.push(this.fb.group({
        key: [c.key],
        name: [c.name],
        isChecked: [isChecked],
      }));
    }

    // set order dir
    this.form.get('OrderDir')?.setValue(this.orderDir ?? 'asc', { emitEvent: false });

  }

  /** Toggle a column and assign/remove sequence */
  onToggle(
    groupValue: { key: string; name: string; isChecked: boolean },
    checked: boolean
  ) {
    const key = groupValue.key;





    if (checked) {
      // user CHECKED it → assign next sequence (if not already assigned)
      if (!this.sequenceMap.has(key)) {
        this.sequenceMap.set(key, ++this.sequenceCounter);
      }
    } else {
      // user UNCHECKED it → remove and re-normalize remaining sequences
      this.sequenceMap.delete(key);
      this.normalizeSequences();
    }


    // max selected 
    if (this.sequenceCounter >= this.maxSelected) {
      this.sortColumns.controls
        .filter(ctrl => !ctrl.get('isChecked')?.value)
        .forEach(ctrl => ctrl.disable());
    } else {
      this.sortColumns.controls
        .forEach(ctrl => ctrl.enable());
    }

  }

  /** Re-label sequences 1..n in current selection order (by original sequence order) */
  private normalizeSequences() {
    const ordered = [...this.sequenceMap.entries()]
      .sort((a, b) => a[1] - b[1])
      .map(([k], idx) => [k, idx + 1] as const);

    this.sequenceMap.clear();
    for (const [k, seq] of ordered) this.sequenceMap.set(k, seq);
    this.sequenceCounter = ordered.length;
  }

  getSortSequence(key: string): number | '' {
    return this.sequenceMap.get(key) ?? '';
  }

  applySort() {
    const selected = this.sortColumns.controls
      .map(c => c.value as { key: string; name: string; isChecked: boolean })
      .filter(v => v.isChecked)
      .map(v => ({
        key: v.key,
        name: v.name,
        sequence: this.sequenceMap.get(v.key) ?? 0
      }))
      .sort((a, b) => a.sequence - b.sequence);

    const payload: ApplySortPayload = {
      orderDir: this.form.get('OrderDir')?.value ?? 'asc',
      columns: selected,
    };

    this.apply.emit(payload);
  }

  clearSort() {
    // uncheck all
    this.sortColumns.controls.forEach(ctrl => ctrl.get('isChecked')?.setValue(false));
    this.sortColumns.controls.forEach(ctrl => ctrl.enable());
    this.sequenceMap.clear();
    this.sequenceCounter = 0;
    // reset order dir if you want
    this.form.get('OrderDir')?.setValue('asc');
    this.clear.emit();
  }
}
